import { useState } from 'react';
import { useDebounce } from '~/hooks/useDebounce';

export const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

type DebouncedSearchInput = {
  searchInputValue: string;
  activeSearch: string;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setSearch: (value: string) => void;
};

/**
 * Backs a search box whose keystrokes render immediately but whose query fires on a debounce.
 *
 * @param delay debounce in ms before `activeSearch` catches up to `searchInputValue`
 * @param initialValue search text of the first render
 * @returns `searchInputValue` for the input's `value`, `activeSearch` to pass to the query hook,
 *     `handleSearchChange` for the input's `onChange`, and `setSearch` to fill both at once when
 *     the value comes from somewhere other than typing, such as picking a suggestion
 *
 * @example
 *   const { searchInputValue, activeSearch, handleSearchChange } = useDebouncedSearchInput();
 *   const { data } = useThingsPaginated(20, activeSearch);
 */
export function useDebouncedSearchInput(
  delay: number = DEFAULT_SEARCH_DEBOUNCE_MS,
  initialValue = ''
): DebouncedSearchInput {
  const [searchInputValue, setSearchInputValue] = useState(initialValue);
  const [activeSearch, setActiveSearch] = useState(initialValue);

  const debouncedSetActiveSearch = useDebounce((value: string) => {
    setActiveSearch(value);
  }, delay);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchInputValue(newValue);
    debouncedSetActiveSearch(newValue);
  };

  const setSearch = (value: string) => {
    setSearchInputValue(value);
    setActiveSearch(value);
    debouncedSetActiveSearch.cancel();
  };

  return { searchInputValue, activeSearch, handleSearchChange, setSearch };
}
